// frontend/src/pages/Profile.jsx
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import api from "../config/api";
import postService from "../services/postService";
import EditPostModal from "../components/EditPostModal";
import PublicationCounter from "../components/PublicationCounter";
import UpdateNumeroCasa from "../components/UpdateNumeroCasa";
import ResidentApproval from "../components/ResidentApproval";
import UserTypeBadge from "../components/UserTypeBadge"; // 👈 NUEVO
import Swal from "sweetalert2";
import {
  User,
  Phone,
  Mail,
  CreditCard,
  MapPin,
  Building2,
  Save,
  AlertTriangle,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Package,
  Edit2,
  Plus,
  Eye,
  TrendingUp,
  Heart,
  Power,
  MessageCircle,
} from "lucide-react";

export default function Profile() {
  const { user: authUser, updateUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    apelido: "",
    phone: "",
    email: "",
  });
  const [requests, setRequests] = useState([]);
  const [showRequests, setShowRequests] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [editingPost, setEditingPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchProfile();
    fetchRequests();
    fetchUserPosts();
    fetchUnreadCount();
  }, []);

  const fetchUnreadCount = async () => {
    if (!authUser) return;
    try {
      const response = await api.get("/chat/unread-count");
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editPostId = params.get("edit");
    const tab = params.get("tab");

    if (tab === "posts") {
      setActiveTab("posts");
    }

    if (editPostId && posts.length > 0) {
      const postToEdit = posts.find((p) => p.id === parseInt(editPostId));
      if (postToEdit) {
        setEditingPost(postToEdit);
        setIsModalOpen(true);
        window.history.replaceState({}, "", "/perfil");
      }
    }
  }, [posts]);

  const fetchProfile = async () => {
    try {
      const response = await api.get("/user/profile");
      setUser(response.data.user);
      setFormData({
        apelido: response.data.user.apelido || "",
        phone: response.data.user.phone || "",
        email: response.data.user.email || "",
      });
    } catch (error) {
      console.error("Erro ao carregar perfil:", error);
      Swal.fire("Erro!", "Não foi possível carregar o perfil.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const response = await api.get("/user/profile/requests");
      setRequests(response.data);
    } catch (error) {
      console.error("Erro ao carregar solicitações:", error);
    }
  };

  const fetchUserPosts = async () => {
    setLoadingPosts(true);
    try {
      const result = await postService.getUserPosts();
      if (result.success) {
        setPosts(result.data);
      } else {
        console.error("Error:", result.error);
      }
    } catch (error) {
      console.error("Erro ao carregar publicações:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    const hasChanges =
      formData.apelido !== (user?.apelido || "") ||
      formData.phone !== (user?.phone || "") ||
      formData.email !== (user?.email || "");

    if (!hasChanges) {
      Swal.fire({
        icon: "info",
        title: "Nenhuma alteração",
        text: "Você não fez nenhuma alteração nos dados.",
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    setSaving(true);

    try {
      const response = await api.put("/user/profile", formData);
      setUser(response.data.user);

      if (updateUser) {
        updateUser(response.data.user);
      } else {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const currentUser = JSON.parse(storedUser);
          currentUser.apelido = response.data.user.apelido;
          currentUser.phone = response.data.user.phone;
          currentUser.email = response.data.user.email;
          localStorage.setItem("user", JSON.stringify(currentUser));
        }
        window.dispatchEvent(
          new CustomEvent("user-updated", {
            detail: response.data.user,
          })
        );
      }

      Swal.fire({
        icon: "success",
        title: "Perfil atualizado!",
        text: "Suas informações foram atualizadas com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Erro ao salvar:", error);
      const message =
        error.response?.data?.message || "Não foi possível atualizar o perfil.";
      Swal.fire("Erro!", message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsSold = async (postId) => {
    const result = await Swal.fire({
      title: "Marcar como vendido?",
      text: "Este anúncio será marcado como vendido e não aparecerá mais na lista de produtos disponíveis.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#449879",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, marcar como vendido",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await postService.markAsSold(postId);
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Produto marcado como vendido!",
            text: "O anúncio foi atualizado.",
            timer: 2000,
            showConfirmButton: false,
          });
          fetchUserPosts();
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        Swal.fire(
          "Erro!",
          "Não foi possível marcar o produto como vendido.",
          "error"
        );
      }
    }
  };

  const handleDeletePost = async (postId) => {
    const result = await Swal.fire({
      title: "Excluir anúncio?",
      text: "Esta ação não pode ser desfeita. O anúncio será removido permanentemente.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await postService.deletePost(postId);
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Anúncio excluído!",
            text: "O anúncio foi removido com sucesso.",
            timer: 2000,
            showConfirmButton: false,
          });
          fetchUserPosts();
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        Swal.fire("Erro!", "Não foi possível excluir o anúncio.", "error");
      }
    }
  };

  const handleUpdatePost = async (formData, postId) => {
    try {
      const response = await postService.updatePost(postId, formData);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Anúncio atualizado!",
          text: "Suas alterações foram salvas com sucesso.",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchUserPosts();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      Swal.fire(
        "Erro!",
        error.message || "Não foi possível atualizar o anúncio.",
        "error"
      );
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setIsModalOpen(true);
  };

  const handleToggleActive = async (post) => {
    const action = post.is_active ? "desativar" : "ativar";
    const actionText = post.is_active ? "desativado" : "ativado";

    const result = await Swal.fire({
      title: `${action === "ativar" ? "Ativar" : "Desativar"} anúncio?`,
      html: `
        <div class="text-left">
          <p class="text-sm text-gray-600 mb-2">
            ${
              action === "ativar"
                ? "Este anúncio ficará visível novamente na home e outros usuários poderão vê-lo."
                : "Este anúncio ficará oculto da home. Você poderá ativá-lo novamente a qualquer momento."
            }
          </p>
          <p class="text-sm text-amber-600 mt-2">
            ⚠️ ${
              action === "ativar"
                ? "O anúncio voltará a aparecer para todos."
                : "O anúncio não será excluído, apenas ficará indisponível temporariamente."
            }
          </p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: post.is_active ? "#eab308" : "#449879",
      cancelButtonColor: "#6b7280",
      confirmButtonText: `Sim, ${action}`,
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        const response = await postService.toggleActive(post.id);
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: `Anúncio ${actionText}!`,
            text: `O anúncio "${post.titulo}" foi ${actionText} com sucesso.`,
            timer: 2000,
            showConfirmButton: false,
          });
          fetchUserPosts();
        } else {
          throw new Error(response.error);
        }
      } catch (error) {
        Swal.fire("Erro!", `Não foi possível ${action} o anúncio.`, "error");
      }
    }
  };

  const getStatusBadge = (status, isSold, isActive) => {
    if (isSold) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          <CheckCircle className="w-3 h-3" /> Vendido
        </span>
      );
    }

    if (isActive === false) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
          <XCircle className="w-3 h-3" /> Indisponível
        </span>
      );
    }

    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" /> Pendente
          </span>
        );
      case "aprovado":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" /> Disponível
          </span>
        );
      case "rejeitado":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" /> Rejeitado
          </span>
        );
      default:
        return null;
    }
  };

  const showRequestUpdateModal = () => {
    Swal.fire({
      title: "Solicitar alteração de dados",
      html: `
        <div class="text-left">
          <p class="text-sm text-gray-600 mb-3">Os seguintes dados NÃO podem ser alterados diretamente:</p>
          <ul class="text-sm text-gray-500 mb-4 list-disc list-inside">
            <li>Nome completo</li>
            <li>CPF</li>
            <li>Endereço completo</li>
            <li>Condomínio</li>
          </ul>
          <p class="text-sm text-gray-600 mb-2">Qual campo deseja alterar?</p>
          <select id="field" class="swal2-select mb-3">
            <option value="name">Nome completo</option>
            <option value="cpf">CPF</option>
            <option value="endereco">Endereço</option>
          </select>
          <input type="text" id="new_value" class="swal2-input" placeholder="Novo valor">
          <textarea id="reason" class="swal2-textarea" placeholder="Motivo da solicitação (mínimo 10 caracteres)" rows="3"></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#eab308",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Enviar solicitação",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const field = document.getElementById("field").value;
        const newValue = document.getElementById("new_value").value;
        const reason = document.getElementById("reason").value;

        if (!newValue) {
          Swal.showValidationMessage("Informe o novo valor");
          return false;
        }
        if (!reason || reason.length < 10) {
          Swal.showValidationMessage("Informe o motivo (mínimo 10 caracteres)");
          return false;
        }

        return { field, newValue, reason };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.post("/user/profile/request-update", {
            requested_fields: [result.value.field],
            new_data: { [result.value.field]: result.value.newValue },
            reason: result.value.reason,
          });

          Swal.fire({
            icon: "success",
            title: "Solicitação enviada!",
            text: "O administrador irá analisar sua solicitação.",
            confirmButtonColor: "#449879",
          });
          fetchRequests();
        } catch (error) {
          Swal.fire("Erro!", "Não foi possível enviar a solicitação.", "error");
        }
      }
    });
  };

  const showDeleteAccountModal = () => {
    Swal.fire({
      title: "Solicitar exclusão de conta",
      html: `
        <div class="text-left">
          <p class="text-sm text-red-600 mb-3">⚠️ Esta ação é irreversível!</p>
          <p class="text-sm text-gray-600 mb-3">Todos os seus dados, anúncios e histórico serão removidos permanentemente.</p>
          <textarea id="reason" class="swal2-textarea" placeholder="Motivo da exclusão (mínimo 10 caracteres)" rows="3"></textarea>
          <label class="flex items-center gap-2 mt-3">
            <input type="checkbox" id="confirm" class="w-4 h-4">
            <span class="text-sm text-gray-600">Confirmo que desejo excluir minha conta permanentemente</span>
          </label>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Solicitar exclusão",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const reason = document.getElementById("reason").value;
        const confirm = document.getElementById("confirm").checked;

        if (!reason || reason.length < 10) {
          Swal.showValidationMessage("Informe o motivo (mínimo 10 caracteres)");
          return false;
        }
        if (!confirm) {
          Swal.showValidationMessage("Confirme que deseja excluir sua conta");
          return false;
        }

        return { reason };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.post("/user/profile/request-delete", {
            reason: result.value.reason,
            confirm: true,
          });

          Swal.fire({
            icon: "success",
            title: "Solicitação enviada!",
            text: "O administrador irá analisar sua solicitação de exclusão.",
            confirmButtonColor: "#449879",
          });
          fetchRequests();
        } catch (error) {
          Swal.fire("Erro!", "Não foi possível enviar a solicitação.", "error");
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#449879] mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-2 pb-24 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Meu Perfil</h1>
          <p className="text-sm text-gray-500">
            Gerencie suas informações e anúncios
          </p>
        </div>

        {/* 🔥 TABS */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("profile")}
            className={`px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 ${
              activeTab === "profile"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <User className="w-4 h-4" />
            Dados Pessoais
          </button>
          <button
            onClick={() => setActiveTab("posts")}
            className={`px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 ${
              activeTab === "posts"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Package className="w-4 h-4" />
            Meus Anúncios
            {posts.length > 0 && (
              <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                {posts.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("chat")}
            className={`px-6 py-3 font-medium text-sm rounded-t-lg transition-all flex items-center gap-2 ${
              activeTab === "chat"
                ? "text-[#449879] border-b-2 border-[#449879] bg-white"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            Chat
            {unreadCount > 0 && (
              <span className="ml-1 text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Tab: Dados Pessoais */}
        {activeTab === "profile" && (
          <>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <div className="p-6">
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#449879] to-[#2d5f4d] rounded-2xl flex items-center justify-center text-white font-bold text-2xl">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h2 className="text-xl font-semibold text-gray-800">
                        {user?.name}
                      </h2>
                      <UserTypeBadge
                        tipoResidente={user?.tipo_residente}
                        size="md"
                      />
                    </div>
                    <p className="text-sm text-gray-500">
                      Membro desde{" "}
                      {new Date(user?.created_at).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                </div>
                <UpdateNumeroCasa />
                <form onSubmit={handleUpdateProfile} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome completo
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={user?.name || ""}
                        disabled
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Para alterar o nome, solicite ao administrador.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apelido{" "}
                      <span className="text-xs text-gray-400">(opcional)</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.apelido}
                        onChange={(e) =>
                          setFormData({ ...formData, apelido: e.target.value })
                        }
                        placeholder="Como você quer ser chamado(a)?"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WhatsApp / Telefone
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        placeholder="(11) 99999-9999"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-mail
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        placeholder="seu@email.com"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent bg-white"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full bg-[#449879] text-white py-3 rounded-xl font-medium hover:bg-[#2d5f4d] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? "Salvando..." : "Salvar alterações"}
                  </button>
                </form>
              </div>
            </div>

            {/* 🔥 CONTADOR DE PUBLICACIONES */}
            <div className="mb-6">
              <PublicationCounter />
            </div>

            {/* 🔥 APROBACIÓN DE MORADORES (solo para propietarios) */}
            {user?.tipo_residente === "proprietario" && (
              <div className="mb-6">
                <ResidentApproval user={user} />
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <div className="p-6 border-b border-gray-100">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500" />
                  Dados bloqueados para edição
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Estes dados não podem ser alterados diretamente. Clique no
                  botão abaixo para solicitar alteração.
                </p>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    CPF
                  </label>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-800">{user?.cpf}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Endereço
                  </label>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-sm text-gray-800">
                      {user?.address?.logradouro},{" "}
                      {user?.unidade?.numero || user?.address?.numero || "S/N"},{" "}
                      {user?.address?.bairro}, {user?.address?.cidade} -{" "}
                      {user?.address?.uf}
                      <br />
                      CEP: {user?.address?.cep}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Condomínio
                  </label>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-800">
                      {user?.condominio?.nome || "Não informado"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6 pt-0">
                <button
                  onClick={showRequestUpdateModal}
                  className="w-full border border-[#449879] text-[#449879] py-2.5 rounded-xl font-medium hover:bg-[#449779]/5 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Solicitar alteração de dados bloqueados
                </button>
              </div>
            </div>

            {requests.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
                <button
                  onClick={() => setShowRequests(!showRequests)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-800">
                    Histórico de solicitações
                  </span>
                  <span className="text-sm text-gray-500">
                    {showRequests ? "▲" : "▼"}
                  </span>
                </button>

                {showRequests && (
                  <div className="p-4 pt-0 space-y-3">
                    {requests.map((req) => (
                      <div key={req.id} className="p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            {req.request_type === "update"
                              ? "Solicitação de alteração"
                              : "Solicitação de exclusão"}
                          </span>
                          {getStatusBadge(req.status, false, true)}
                        </div>
                        <p className="text-xs text-gray-500">
                          Data:{" "}
                          {new Date(req.created_at).toLocaleDateString("pt-BR")}
                        </p>
                        {req.admin_response && (
                          <p className="text-xs text-gray-500 mt-1">
                            Resposta: {req.admin_response}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={showDeleteAccountModal}
              className="w-full border border-red-300 text-red-600 py-3 rounded-xl font-medium hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Solicitar exclusão da conta
            </button>
          </>
        )}

        {/* Tab: Meus Anúncios */}
        {activeTab === "posts" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Package className="w-4 h-4 text-[#449879]" />
                Meus Anúncios
              </h3>
              <button
                onClick={() => (window.location.href = "/publicar")}
                className="bg-[#449879] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[#2d5f4d] transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Novo anúncio
              </button>
            </div>

            {loadingPosts ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#449879] mx-auto mb-4"></div>
                <p className="text-gray-500">Carregando anúncios...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Você ainda não tem anúncios.</p>
                <button
                  onClick={() => (window.location.href = "/publicar")}
                  className="mt-4 text-[#449879] hover:underline"
                >
                  Criar meu primeiro anúncio →
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    className={`p-6 hover:bg-gray-50 transition-colors ${
                      !post.is_active && !post.is_sold
                        ? "opacity-75 bg-gray-50"
                        : ""
                    }`}
                  >
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="relative w-full md:w-32 h-32 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                        {post.fotos && post.fotos.length > 0 ? (
                          <img
                            src={post.fotos[0]}
                            alt={post.titulo}
                            className={`w-full h-full object-cover ${
                              !post.is_active && !post.is_sold
                                ? "opacity-50"
                                : ""
                            }`}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/128?text=Sem+imagem";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-200">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}

                        {!post.is_active && !post.is_sold && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-white text-xs font-bold bg-red-600/80 px-2 py-1 rounded-full">
                              Indisponível
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <h4 className="font-semibold text-gray-800">
                              {post.titulo}
                            </h4>
                            <p className="text-sm text-gray-500 mt-1">
                              {post.categoria}
                            </p>
                            <div className="flex items-center gap-3 mt-2">
                              {post.tipo === "Venda" ? (
                                <span
                                  className={`text-lg font-bold ${
                                    !post.is_active && !post.is_sold
                                      ? "text-gray-500"
                                      : "text-[#449879]"
                                  }`}
                                >
                                  R$ {parseFloat(post.preco).toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-sm text-green-600 flex items-center gap-1">
                                  <Heart className="w-4 h-4" />
                                  Doação
                                </span>
                              )}
                              {getStatusBadge(
                                post.status,
                                post.is_sold,
                                post.is_active
                              )}
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleToggleActive(post)}
                              className={`p-2 rounded-lg transition-colors ${
                                post.is_active
                                  ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                  : "bg-green-100 text-green-600 hover:bg-green-200"
                              }`}
                              title={
                                post.is_active
                                  ? "Desativar anúncio"
                                  : "Ativar anúncio"
                              }
                            >
                              <Power className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditPost(post)}
                              className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                (window.location.href = `/produto/${post.id}`)
                              }
                              className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                              title="Visualizar"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {!post.is_sold && post.status === "aprovado" && (
                              <button
                                onClick={() => handleMarkAsSold(post.id)}
                                className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                                title="Marcar como vendido"
                              >
                                <TrendingUp className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeletePost(post.id)}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <p className="text-sm text-gray-500 mt-3 line-clamp-2">
                          {post.descricao}
                        </p>

                        <p className="text-xs text-gray-400 mt-2">
                          Publicado em{" "}
                          {new Date(post.created_at).toLocaleDateString(
                            "pt-BR"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 🔥 TAB: CHAT */}
        {activeTab === "chat" && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#449879]" />
                Minhas Conversas
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Acesse suas conversas com outros usuários
              </p>
            </div>

            <div className="p-6">
              <Link
                to="/chat"
                className="w-full bg-[#449879] text-white py-3 rounded-xl font-medium hover:bg-[#2d5f4d] transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Ver minhas conversas
              </Link>
            </div>
          </div>
        )}

        {/* Modal de edición */}
        <EditPostModal
          post={editingPost}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingPost(null);
          }}
          onSuccess={handleUpdatePost}
        />
      </div>
    </div>
  );
}
